/* istanbul ignore file */
/* tslint:disable */
 
import type { Organization } from '../models/Organization';
import type { OrganizationAccess } from '../models/OrganizationAccess';
import type { OrganizationAccessBlock } from '../models/OrganizationAccessBlock';
import type { OrganizationInviteUser } from '../models/OrganizationInviteUser';
import type { OrganizationOrganigrama } from '../models/OrganizationOrganigrama';
import type { OrganizationShort } from '../models/OrganizationShort';
import type { OrganizationUsers } from '../models/OrganizationUsers';
import type { PaginatedOrganizationList } from '../models/PaginatedOrganizationList';
import type { PaginatedOrganizationUsersList } from '../models/PaginatedOrganizationUsersList';
import type { PaginatedRoleList } from '../models/PaginatedRoleList';
import type { PaginatedTeamListList } from '../models/PaginatedTeamListList';
import type { PaginatedTeamUsersDetailList } from '../models/PaginatedTeamUsersDetailList';
import type { PatchedOrganization } from '../models/PatchedOrganization';
import type { PatchedOrganizationAccess } from '../models/PatchedOrganizationAccess';
import type { PatchedRole } from '../models/PatchedRole';
import type { PatchedTeamModify } from '../models/PatchedTeamModify';
import type { PatchedTeamUsersModify } from '../models/PatchedTeamUsersModify';
import type { Role } from '../models/Role';
import type { TeamList } from '../models/TeamList';
import type { TeamModify } from '../models/TeamModify';
import type { TeamUsers } from '../models/TeamUsers';
import type { TeamUsersDetail } from '../models/TeamUsersDetail';
import type { TeamUsersModify } from '../models/TeamUsersModify';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class OrganizationsService {

  /**
   * @param ordering Which field to use when ordering the results.
   * @param organization 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param search A search term.
   * @param status * `online` - Online
 * * `offline` - Offline
 * * `all` - All
   * @param team 
   * @param user 
   * @returns PaginatedOrganizationUsersList 
   * @throws ApiError
   */
  public static organizationsAccessList(
ordering?: string,
organization?: number,
page?: number,
perPage?: number,
search?: string,
status?: 'all' | 'offline' | 'online',
team?: Array<number>,
user?: number,
): CancelablePromise<PaginatedOrganizationUsersList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/access/',
      query: {
        'ordering': ordering,
        'organization': organization,
        'page': page,
        'per_page': perPage,
        'search': search,
        'status': status,
        'team': team,
        'user': user,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this organization access.
   * @returns OrganizationUsers 
   * @throws ApiError
   */
  public static organizationsAccessRetrieve(
id: number,
): CancelablePromise<OrganizationUsers> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/access/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this organization access.
   * @param requestBody 
   * @returns OrganizationAccess 
   * @throws ApiError
   */
  public static organizationsAccessPartialUpdate(
id: number,
requestBody?: PatchedOrganizationAccess,
): CancelablePromise<OrganizationAccess> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/organizations/access/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this organization access.
   * @returns void 
   * @throws ApiError
   */
  public static organizationsAccessDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/organizations/access/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this organization access.
   * @param requestBody 
   * @returns OrganizationUsers 
   * @throws ApiError
   */
  public static organizationsAccessBlockCreate(
id: number,
requestBody: OrganizationAccessBlock,
): CancelablePromise<OrganizationUsers> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/organizations/access/{id}/block/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param requestBody 
   * @param ordering Which field to use when ordering the results.
   * @param organization 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param search A search term.
   * @param status * `online` - Online
 * * `offline` - Offline
 * * `all` - All
   * @param team 
   * @param user 
   * @returns PaginatedOrganizationUsersList 
   * @throws ApiError
   */
  public static organizationsAccessAddCreate(
requestBody: OrganizationInviteUser,
ordering?: string,
organization?: number,
page?: number,
perPage?: number,
search?: string,
status?: 'all' | 'offline' | 'online',
team?: Array<number>,
user?: number,
): CancelablePromise<PaginatedOrganizationUsersList> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/organizations/access/add/',
      query: {
        'ordering': ordering,
        'organization': organization,
        'page': page,
        'per_page': perPage,
        'search': search,
        'status': status,
        'team': team,
        'user': user,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Returns the organigrama (organization chart) for a given organization and optional head_user.
 * Query params:
 * - organization: int (required)
 * - head_user: int (optional)
   * @param organization 
   * @param headUser 
   * @returns OrganizationOrganigrama 
   * @throws ApiError
   */
  public static organizationsAccessOrganigramaList(
organization: number,
headUser?: number | null,
): CancelablePromise<Array<OrganizationOrganigrama>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/access/organigrama/',
      query: {
        'head_user': headUser,
        'organization': organization,
      },
    });
  }

  /**
   * @param domainName 
   * @returns OrganizationShort 
   * @throws ApiError
   */
  public static organizationsInfoRetrieve(
domainName: string,
): CancelablePromise<OrganizationShort> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/info/{domain_name}/',
      path: {
        'domain_name': domainName,
      },
    });
  }

  /**
   * @param ids comma separated list
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedOrganizationList 
   * @throws ApiError
   */
  public static organizationsOrganizationsList(
ids?: string,
ordering?: string,
page?: number,
perPage?: number,
search?: string,
): CancelablePromise<PaginatedOrganizationList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/organizations/',
      query: {
        'ids': ids,
        'ordering': ordering,
        'page': page,
        'per_page': perPage,
        'search': search,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns Organization 
   * @throws ApiError
   */
  public static organizationsOrganizationsCreate(
requestBody: Organization,
): CancelablePromise<Organization> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/organizations/organizations/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this organization.
   * @returns Organization 
   * @throws ApiError
   */
  public static organizationsOrganizationsRetrieve(
id: number,
): CancelablePromise<Organization> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/organizations/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this organization.
   * @param requestBody 
   * @returns Organization 
   * @throws ApiError
   */
  public static organizationsOrganizationsPartialUpdate(
id: number,
requestBody?: PatchedOrganization,
): CancelablePromise<Organization> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/organizations/organizations/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this organization.
   * @returns void 
   * @throws ApiError
   */
  public static organizationsOrganizationsDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/organizations/organizations/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Team roles
   * @param ids comma separated list
   * @param ordering Which field to use when ordering the results.
   * @param organization 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param search 
   * @param team 
   * @returns PaginatedRoleList 
   * @throws ApiError
   */
  public static organizationsTeamRolesList(
ids?: string,
ordering?: string,
organization?: number,
page?: number,
perPage?: number,
search?: string,
team?: Array<number>,
): CancelablePromise<PaginatedRoleList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/team-roles/',
      query: {
        'ids': ids,
        'ordering': ordering,
        'organization': organization,
        'page': page,
        'per_page': perPage,
        'search': search,
        'team': team,
      },
    });
  }

  /**
   * Team roles
   * @param requestBody 
   * @returns Role 
   * @throws ApiError
   */
  public static organizationsTeamRolesCreate(
requestBody: Role,
): CancelablePromise<Role> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/organizations/team-roles/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Team roles
   * @param id A unique integer value identifying this role.
   * @returns Role 
   * @throws ApiError
   */
  public static organizationsTeamRolesRetrieve(
id: number,
): CancelablePromise<Role> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/team-roles/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Team roles
   * @param id A unique integer value identifying this role.
   * @param requestBody 
   * @returns Role 
   * @throws ApiError
   */
  public static organizationsTeamRolesPartialUpdate(
id: number,
requestBody?: PatchedRole,
): CancelablePromise<Role> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/organizations/team-roles/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Team roles
   * @param id A unique integer value identifying this role.
   * @returns void 
   * @throws ApiError
   */
  public static organizationsTeamRolesDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/organizations/team-roles/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Team membership with role
   * @param ordering Which field to use when ordering the results.
   * @param organization 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param search 
   * @param team 
   * @param user 
   * @returns PaginatedTeamUsersDetailList 
   * @throws ApiError
   */
  public static organizationsTeamUsersList(
ordering?: string,
organization?: number,
page?: number,
perPage?: number,
search?: string,
team?: number,
user?: number,
): CancelablePromise<PaginatedTeamUsersDetailList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/team-users/',
      query: {
        'ordering': ordering,
        'organization': organization,
        'page': page,
        'per_page': perPage,
        'search': search,
        'team': team,
        'user': user,
      },
    });
  }

  /**
   * Team membership with role
   * @param requestBody 
   * @returns TeamUsers 
   * @throws ApiError
   */
  public static organizationsTeamUsersCreate(
requestBody: TeamUsers,
): CancelablePromise<TeamUsers> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/organizations/team-users/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Team membership with role
   * @param id A unique integer value identifying this team users.
   * @returns TeamUsersDetail 
   * @throws ApiError
   */
  public static organizationsTeamUsersRetrieve(
id: number,
): CancelablePromise<TeamUsersDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/team-users/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Team membership with role
   * @param id A unique integer value identifying this team users.
   * @param requestBody 
   * @returns TeamUsersModify 
   * @throws ApiError
   */
  public static organizationsTeamUsersPartialUpdate(
id: number,
requestBody?: PatchedTeamUsersModify,
): CancelablePromise<TeamUsersModify> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/organizations/team-users/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Team membership with role
   * @param id A unique integer value identifying this team users.
   * @returns void 
   * @throws ApiError
   */
  public static organizationsTeamUsersDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/organizations/team-users/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param excludeType * `branch` - Filială
 * * `department` - Departament
 * * `section` - Secțiune
 * * `logical_node` - Logical node
   * @param hasParent 
   * @param ids comma separated list
   * @param myTeams 
   * @param ordering Which field to use when ordering the results.
   * @param organization 
   * @param page A page number within the paginated result set.
   * @param parent 
   * @param perPage Number of results to return per page.
   * @param search 
   * @param type * `branch` - Filială
 * * `department` - Departament
 * * `section` - Secțiune
 * * `logical_node` - Logical node
   * @returns PaginatedTeamListList 
   * @throws ApiError
   */
  public static organizationsTeamsList(
excludeType?: Array<'branch' | 'department' | 'logical_node' | 'section' | null>,
hasParent?: boolean,
ids?: string,
myTeams?: boolean,
ordering?: string,
organization?: number,
page?: number,
parent?: number,
perPage?: number,
search?: string,
type?: Array<'branch' | 'department' | 'logical_node' | 'section' | null>,
): CancelablePromise<PaginatedTeamListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/teams/',
      query: {
        'exclude_type': excludeType,
        'has_parent': hasParent,
        'ids': ids,
        'my_teams': myTeams,
        'ordering': ordering,
        'organization': organization,
        'page': page,
        'parent': parent,
        'per_page': perPage,
        'search': search,
        'type': type,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns TeamModify 
   * @throws ApiError
   */
  public static organizationsTeamsCreate(
requestBody: TeamModify,
): CancelablePromise<TeamModify> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/organizations/teams/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this team.
   * @returns TeamList 
   * @throws ApiError
   */
  public static organizationsTeamsRetrieve(
id: number,
): CancelablePromise<TeamList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/organizations/teams/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this team.
   * @param requestBody 
   * @returns TeamModify 
   * @throws ApiError
   */
  public static organizationsTeamsPartialUpdate(
id: number,
requestBody?: PatchedTeamModify,
): CancelablePromise<TeamModify> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/organizations/teams/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this team.
   * @returns void 
   * @throws ApiError
   */
  public static organizationsTeamsDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/organizations/teams/{id}/',
      path: {
        'id': id,
      },
    });
  }

}
